import { Suspense, lazy, useEffect } from "react";
import ScrollToTop from "./helpers/scroll-top";
import {
  ADMIN_ROLES,
  CATEGORY_MANAGE_ROLES,
  CUSTOMER_ORDER_EDIT_ROLES,
  DASHBOARD_ROLES,
  INVENTORY_ROLES,
  LEAD_ROLES,
  MANAGEMENT_ROLES,
  MESSAGING_SETTINGS_ROLES,
  NOTIFICATION_ROLES,
  ORDER_ROLES,
  ORDER_VIEW_ROLES,
  PRODUCT_EDIT_ROLES,
  PRODUCT_MANAGE_ROLES,
  PRODUCT_ROLES,
  REPORTS_ROLES,
  RESELLER_ORDER_EDIT_ROLES,
  RESELLER_PO_INVOICE_PRINT_ROLES,
  RESELLER_PURCHASE_ORDER_ROLES,
  ROLE_HOME_PATH,
  STOCK_MOVEMENT_ROLES,
  SUPER_ADMIN_ONLY
} from "./helpers/roles";
import {
  fetchApiTest,
  fetchShopCart
} from "./services/api";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RoleRoute from "./components/auth/RoleRoute";
import { useDispatch } from "react-redux";
import { loadShopCatalog } from "./helpers/shop-catalog";
import { normalizeShopCart } from "./helpers/aa-cart";
import { deleteAllFromCart, setCartError, setCartFromApi } from "./store/slices/cart-slice";
import { AddToCartProvider } from "./context/AddToCartContext";

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeFashionEight = lazy(() => import("./pages/home/HomeFashionEight"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const HomeFurnitureThree = lazy(() =>
  import("./pages/home/HomeFurnitureThree")
);
const HomeFurnitureFour = lazy(() => import("./pages/home/HomeFurnitureFour"));
const HomeFurnitureFive = lazy(() => import("./pages/home/HomeFurnitureFive"));
const HomeFurnitureSix = lazy(() => import("./pages/home/HomeFurnitureSix"));
const HomeFurnitureSeven = lazy(() =>
  import("./pages/home/HomeFurnitureSeven")
);
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() =>
  import("./pages/home/HomeElectronicsTwo")
);
const HomeElectronicsThree = lazy(() =>
  import("./pages/home/HomeElectronicsThree")
);
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomeBookStoreTwo = lazy(() => import("./pages/home/HomeBookStoreTwo"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeFlowerShopTwo = lazy(() => import("./pages/home/HomeFlowerShopTwo"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() =>
  import("./pages/home/HomeOrganicFoodTwo")
);
const HomeOnepageScroll = lazy(() => import("./pages/home/HomeOnepageScroll"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));
const HomeMedicalEquipment = lazy(() =>
  import("./pages/home/HomeMedicalEquipment")
);
const HomeChristmas = lazy(() => import("./pages/home/HomeChristmas"));
const HomeBlackFriday = lazy(() => import("./pages/home/HomeBlackFriday"));
const HomeBlackFridayTwo = lazy(() =>
  import("./pages/home/HomeBlackFridayTwo")
);
const HomeValentinesDay = lazy(() => import("./pages/home/HomeValentinesDay"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() =>
  import("./pages/shop/ShopGridRightSidebar")
);
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() =>
  import("./pages/shop-product/ProductTabLeft")
);
const ProductTabRight = lazy(() =>
  import("./pages/shop-product/ProductTabRight")
);
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() =>
  import("./pages/shop-product/ProductFixedImage")
);

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const Register = lazy(() => import("./pages/other/Register"));
const CustomerProfile = lazy(() => import("./pages/other/CustomerProfile"));
const CustomerDashboard = lazy(() => import("./pages/other/CustomerDashboard"));
const CustomerOrderView = lazy(() => import("./pages/other/CustomerOrderView"));
const CustomerAddresses = lazy(() => import("./pages/other/CustomerAddresses"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserCreate = lazy(() => import("./pages/admin/AdminUserCreate"));
const AdminUserView = lazy(() => import("./pages/admin/AdminUserView"));
const AdminUserDetails = lazy(() => import("./pages/admin/AdminUserDetails"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderView = lazy(() => import("./pages/admin/AdminOrderView"));
const AdminOrderEdit = lazy(() => import("./pages/admin/AdminOrderEdit"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminLeadView = lazy(() => import("./pages/admin/AdminLeadView"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductPriceListPrint = lazy(() => import("./pages/admin/AdminProductPriceListPrint"));
const AdminProductCreate = lazy(() => import("./pages/admin/AdminProductCreate"));
const AdminProductView = lazy(() => import("./pages/admin/AdminProductView"));
const AdminProductEdit = lazy(() => import("./pages/admin/AdminProductEdit"));
const AdminProductCategories = lazy(() => import("./pages/admin/AdminProductCategories"));
const AdminProductCategoryCreate = lazy(() => import("./pages/admin/AdminProductCategoryCreate"));
const AdminProductCategoryEdit = lazy(() => import("./pages/admin/AdminProductCategoryEdit"));
const AdminMexicoLocations = lazy(() => import("./pages/admin/AdminMexicoLocations"));
const AdminBankData = lazy(() => import("./pages/admin/AdminBankData"));
const AdminResellerTerritories = lazy(() => import("./pages/admin/AdminResellerTerritories"));
const AdminGoogleLocations = lazy(() => import("./pages/admin/AdminGoogleLocations"));
const AdminCatalogSettings = lazy(() => import("./pages/admin/AdminCatalogSettings"));
const AdminErpSync = lazy(() => import("./pages/admin/AdminErpSync"));
const AdminResellerDiscountTiers = lazy(() => import("./pages/admin/AdminResellerDiscountTiers"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminStockMovements = lazy(() => import("./pages/admin/AdminStockMovements"));
const AdminInventoryResellerStock = lazy(() => import("./pages/admin/AdminInventoryResellerStock"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminProductPriceLogs = lazy(() => import("./pages/admin/AdminProductPriceLogs"));
const AdminResellerPurchaseOrders = lazy(() => import("./pages/admin/AdminResellerPurchaseOrders"));
const AdminResellerPurchaseOrderView = lazy(() => import("./pages/admin/AdminResellerPurchaseOrderView"));
const AdminResellerPurchaseOrderEdit = lazy(() => import("./pages/admin/AdminResellerPurchaseOrderEdit"));
const AdminResellerPurchaseOrderInvoicePrint = lazy(() => import("./pages/admin/AdminResellerPurchaseOrderInvoicePrint"));
const AdminStoreSettings = lazy(() => import("./pages/admin/AdminStoreSettings"));
const AdminEmailSettings = lazy(() => import("./pages/admin/AdminEmailSettings"));
const AdminResellerEmailConfigs = lazy(() => import("./pages/admin/AdminResellerEmailConfigs"));
const AdminResellerEmailConfigForm = lazy(() => import("./pages/admin/AdminResellerEmailConfigForm"));
const AdminWhatsAppSettings = lazy(() => import("./pages/admin/AdminWhatsAppSettings"));
const AdminResellerWhatsAppConfigs = lazy(() => import("./pages/admin/AdminResellerWhatsAppConfigs"));
const AdminResellerWhatsAppConfigForm = lazy(() => import("./pages/admin/AdminResellerWhatsAppConfigForm"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Checkout = lazy(() => import("./pages/other/Checkout"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

const LegacyProductRedirect = () => {
  const { id } = useParams();

  return (
    <Navigate
      to={process.env.PUBLIC_URL + "/product-tab-bottom/" + id}
      replace
    />
  );
};

// Keep purchased template demos in codebase, but disable them in the main app flow.
const ENABLE_LEGACY_DEMO_ROUTES = false;

const App = () => {
  const { user, isBootstrapping } = useAuth();
  const dispatch = useDispatch();

  const loginPath = process.env.PUBLIC_URL + "/login-register";
  // Role lists live in helpers/roles.js so this file, AdminSidebar and
  // services/api.js cannot drift apart. See api/docs/role-access-matrix.md.
  const adminRoles = ADMIN_ROLES;
  const managementRoles = MANAGEMENT_ROLES;
  const orderRoles = ORDER_ROLES;
  const customerOrderEditRoles = CUSTOMER_ORDER_EDIT_ROLES;
  const resellerOrderEditRoles = RESELLER_ORDER_EDIT_ROLES;
  const resellerPoInvoicePrintRoles = RESELLER_PO_INVOICE_PRINT_ROLES;
  const productRoles = PRODUCT_ROLES;
  const productManageRoles = PRODUCT_MANAGE_ROLES;
  const productEditRoles = PRODUCT_EDIT_ROLES;
  const categoryManageRoles = CATEGORY_MANAGE_ROLES;
  const inventoryRoles = INVENTORY_ROLES;
  const notificationRoles = NOTIFICATION_ROLES;
  const reportsRoles = REPORTS_ROLES;
  const resellerPurchaseOrderRoles = RESELLER_PURCHASE_ORDER_ROLES;

  const roleHomePath = user ? ROLE_HOME_PATH[user.role] : null;
  const roleHome = process.env.PUBLIC_URL + (roleHomePath || "/");

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (!process.env.REACT_APP_API_URL) return;
    fetchApiTest()
      .then((res) => console.log("[API] /api/test", res.data))
      .catch((err) => console.warn("[API] /api/test", err.message));
  }, []);

  useEffect(() => {
    if (isBootstrapping) return;
    loadShopCatalog({
      dispatch,
      role: user?.role,
      resellerAdminUserId: user?.reseller_admin_user_id,
      silent: false
    });
  }, [dispatch, isBootstrapping, user?.role, user?.reseller_admin_user_id]);

  useEffect(() => {
    if (isBootstrapping) return;

    if (user?.role !== "user" && user?.role !== "reseller_admin") {
      dispatch(deleteAllFromCart());
      return;
    }

    if (user?.role === "user" && !user?.reseller_admin_user_id) {
      dispatch(deleteAllFromCart());
      dispatch(
        setCartError(
          "Your account isn't linked to a reseller yet. Please use the invitation link from your reseller to start shopping."
        )
      );
      return;
    }

    const loadCart = async () => {
      try {
        const cartResponse = await fetchShopCart(user.role);
        dispatch(setCartFromApi(normalizeShopCart(cartResponse)));
      } catch (error) {
        dispatch(deleteAllFromCart());
      }
    };

    loadCart();
  }, [dispatch, isBootstrapping, user?.role, user?.reseller_admin_user_id]);

  return (
      <Router>
        <ScrollToTop>
          <AddToCartProvider>
          <Toaster />
          <Suspense
            fallback={
              <div className="flone-preloader-wrapper">
                <div className="flone-preloader">
                  <span></span>
                  <span></span>
                </div>
              </div>
            }
          >
            <Routes>
              <Route
                path={process.env.PUBLIC_URL + "/"}
                element={<HomeKidsFashion/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/home-kids-fashion"}
                element={<HomeKidsFashion/>}
              />

              {/* Homepages */}
              {ENABLE_LEGACY_DEMO_ROUTES && (
                <>
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion"}
                    element={<HomeFashion/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-two"}
                    element={<HomeFashionTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-three"}
                    element={<HomeFashionThree/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-four"}
                    element={<HomeFashionFour/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-five"}
                    element={<HomeFashionFive/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-six"}
                    element={<HomeFashionSix/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-seven"}
                    element={<HomeFashionSeven/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-fashion-eight"}
                    element={<HomeFashionEight/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-cosmetics"}
                    element={<HomeCosmetics/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture"}
                    element={<HomeFurniture/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-two"}
                    element={<HomeFurnitureTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-three"}
                    element={<HomeFurnitureThree/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-four"}
                    element={<HomeFurnitureFour/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-five"}
                    element={<HomeFurnitureFive/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-six"}
                    element={<HomeFurnitureSix/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-furniture-seven"}
                    element={<HomeFurnitureSeven/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-electronics"}
                    element={<HomeElectronics/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-electronics-two"}
                    element={<HomeElectronicsTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-electronics-three"}
                    element={<HomeElectronicsThree/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-book-store"}
                    element={<HomeBookStore/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-book-store-two"}
                    element={<HomeBookStoreTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-plants"}
                    element={<HomePlants/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-flower-shop"}
                    element={<HomeFlowerShop/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-flower-shop-two"}
                    element={<HomeFlowerShopTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-organic-food"}
                    element={<HomeOrganicFood/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-organic-food-two"}
                    element={<HomeOrganicFoodTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-onepage-scroll"}
                    element={<HomeOnepageScroll/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-grid-banner"}
                    element={<HomeGridBanner/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-auto-parts"}
                    element={<HomeAutoParts/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-cake-shop"}
                    element={<HomeCakeShop/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-handmade"}
                    element={<HomeHandmade/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-pet-food"}
                    element={<HomePetFood/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-medical-equipment"}
                    element={<HomeMedicalEquipment/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-christmas"}
                    element={<HomeChristmas/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-black-friday"}
                    element={<HomeBlackFriday/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-black-friday-two"}
                    element={<HomeBlackFridayTwo/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/home-valentines-day"}
                    element={<HomeValentinesDay/>}
                  />
                </>
              )}

              {/* Shop pages */}
              <Route
                path={process.env.PUBLIC_URL + "/shop-grid-standard"}
                element={
                  <Navigate to={process.env.PUBLIC_URL + "/shop"} replace />
                }
              />
              <Route
                path={process.env.PUBLIC_URL + "/shop"}
                element={<ShopGridStandard/>}
              />
              {ENABLE_LEGACY_DEMO_ROUTES && (
                <>
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-grid-filter"}
                    element={<ShopGridFilter/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-grid-two-column"}
                    element={<ShopGridTwoColumn/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-grid-no-sidebar"}
                    element={<ShopGridNoSidebar/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-grid-full-width"}
                    element={<ShopGridFullWidth/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-grid-right-sidebar"}
                    element={<ShopGridRightSidebar/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-list-standard"}
                    element={<ShopListStandard/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-list-full-width"}
                    element={<ShopListFullWidth/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/shop-list-two-column"}
                    element={<ShopListTwoColumn/>}
                  />
                </>
              )}

              {/* Shop product pages */}
              <Route
                path={process.env.PUBLIC_URL + "/product/:id"}
                element={<LegacyProductRedirect />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/product-tab-bottom/:id"}
                element={<Product />}
              />
              {ENABLE_LEGACY_DEMO_ROUTES && (
                <>
                  <Route
                    path={process.env.PUBLIC_URL + "/product-tab-left/:id"}
                    element={<ProductTabLeft/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/product-tab-right/:id"}
                    element={<ProductTabRight/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/product-sticky/:id"}
                    element={<ProductSticky/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/product-slider/:id"}
                    element={<ProductSlider/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/product-fixed-image/:id"}
                    element={<ProductFixedImage/>}
                  />
                </>
              )}

              {/* Blog pages */}
              {ENABLE_LEGACY_DEMO_ROUTES && (
                <>
                  <Route
                    path={process.env.PUBLIC_URL + "/blog-standard"}
                    element={<BlogStandard/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/blog-no-sidebar"}
                    element={<BlogNoSidebar/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/blog-right-sidebar"}
                    element={<BlogRightSidebar/>}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/blog-details-standard"}
                    element={<BlogDetailsStandard/>}
                  />
                </>
              )}

              {/* Other pages */}
              <Route
                path={process.env.PUBLIC_URL + "/about"}
                element={<About/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/contact"}
                element={<Contact/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/my-account"}
                element={
                  user ? (
                    <Navigate to={roleHome} replace />
                  ) : (
                    <Navigate to={loginPath} replace />
                  )
                }
              />
              <Route
                path={process.env.PUBLIC_URL + "/login-register"}
                element={user ? <Navigate to={roleHome} replace /> : <LoginRegister/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/register"}
                element={user ? <Navigate to={roleHome} replace /> : <Register/>}
              />

              <Route
                path={process.env.PUBLIC_URL + "/cart"}
                element={<Cart/>}
              />
              <Route
                path={process.env.PUBLIC_URL + "/wishlist"}
                element={<Wishlist/>}
              />
              {/* Compare page is intentionally disabled for A&A. */}
              <Route
                path={process.env.PUBLIC_URL + "/compare"}
                element={<Navigate to={process.env.PUBLIC_URL + "/"} replace />}
              />
              <Route
                path={process.env.PUBLIC_URL + "/checkout"}
                element={<Checkout/>}
              />

              <Route element={<ProtectedRoute redirectTo={loginPath} />}>
                <Route element={<RoleRoute allowedRoles={["user"]} redirectTo={process.env.PUBLIC_URL + "/"} />}>
                  <Route
                    path={process.env.PUBLIC_URL + "/user/dashboard"}
                    element={<Navigate to={process.env.PUBLIC_URL + "/customer/dashboard"} replace />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/user/dashbard"}
                    element={<Navigate to={process.env.PUBLIC_URL + "/customer/dashboard"} replace />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/user/profile"}
                    element={<Navigate to={process.env.PUBLIC_URL + "/customer/profile"} replace />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/user/addresses"}
                    element={<Navigate to={process.env.PUBLIC_URL + "/customer/addresses"} replace />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/customer/dashboard"}
                    element={<CustomerDashboard />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/customer/notifications"}
                    element={<AdminNotifications />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/customer/dashboard/orders/:orderId"}
                    element={<CustomerOrderView />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/customer/profile"}
                    element={<CustomerProfile />}
                  />
                  <Route
                    path={process.env.PUBLIC_URL + "/customer/addresses"}
                    element={<CustomerAddresses />}
                  />
                </Route>

                <Route element={<RoleRoute allowedRoles={adminRoles} redirectTo={process.env.PUBLIC_URL + "/"} />}>
                  <Route
                    path={process.env.PUBLIC_URL + "/admin"}
                    element={
                      // The API's dashboard endpoint refuses everyone outside
                      // DASHBOARD_ROLES, so they must not land on it at all --
                      // they go to whatever ROLE_HOME_PATH says instead, which
                      // for `warehouse` is the stock screen rather than a
                      // read-only product list.
                      DASHBOARD_ROLES.includes(user?.role) ? (
                        <AdminDashboard />
                      ) : (
                        <Navigate to={roleHome} replace />
                      )
                    }
                  />
                  <Route element={<RoleRoute allowedRoles={managementRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/users"}
                      element={<AdminUsers />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/users/:userId/view"}
                      element={<AdminUserView />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/users/:userId/edit"}
                      element={<AdminUserDetails />}
                    />
                    <Route element={<RoleRoute allowedRoles={MANAGEMENT_ROLES} redirectTo={process.env.PUBLIC_URL + "/admin/users"} />}>
                      <Route
                        path={process.env.PUBLIC_URL + "/admin/users/create"}
                        element={<AdminUserCreate />}
                      />
                    </Route>
                  </Route>
                  <Route element={<RoleRoute allowedRoles={orderRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/orders"}
                      element={<AdminOrders />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={ORDER_VIEW_ROLES} redirectTo={process.env.PUBLIC_URL + "/admin/orders"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/orders/:orderId/view"}
                      element={<AdminOrderView />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={LEAD_ROLES} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/leads"}
                      element={<AdminLeads />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/leads/:leadId"}
                      element={<AdminLeadView />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={customerOrderEditRoles} redirectTo={process.env.PUBLIC_URL + "/admin/orders"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/orders/:orderId/edit"}
                      element={<AdminOrderEdit />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={SUPER_ADMIN_ONLY} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/product-price-logs"}
                      element={<AdminProductPriceLogs />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={productRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/products"}
                      element={<AdminProducts />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/products/price-list/print"}
                      element={<AdminProductPriceListPrint />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/products/:productId/view"}
                      element={<AdminProductView />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={productManageRoles} redirectTo={process.env.PUBLIC_URL + "/admin/products"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/products/create"}
                      element={<AdminProductCreate />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={productEditRoles} redirectTo={process.env.PUBLIC_URL + "/admin/products"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/products/:productId/edit"}
                      element={<AdminProductEdit />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={["reseller_admin"]} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/store-settings"}
                      element={<AdminStoreSettings />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={MESSAGING_SETTINGS_ROLES} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/email-settings"}
                      element={<AdminEmailSettings />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/whatsapp-settings"}
                      element={<AdminWhatsAppSettings />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={categoryManageRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/product-categories"}
                      element={<AdminProductCategories />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/product-categories/create"}
                      element={<AdminProductCategoryCreate />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/product-categories/:categoryId/edit"}
                      element={<AdminProductCategoryEdit />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={SUPER_ADMIN_ONLY} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/mexico-locations"}
                      element={<AdminMexicoLocations />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/bank-data"}
                      element={<AdminBankData />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-territories"}
                      element={<AdminResellerTerritories />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/google-locations"}
                      element={<AdminGoogleLocations />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/catalog-settings"}
                      element={<AdminCatalogSettings />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/erp-sync"}
                      element={<AdminErpSync />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-discount-tiers"}
                      element={<AdminResellerDiscountTiers />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-email-configs"}
                      element={<AdminResellerEmailConfigs />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-email-configs/create"}
                      element={<AdminResellerEmailConfigForm />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-email-configs/:configId/edit"}
                      element={<AdminResellerEmailConfigForm />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-whatsapp-configs"}
                      element={<AdminResellerWhatsAppConfigs />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-whatsapp-configs/create"}
                      element={<AdminResellerWhatsAppConfigForm />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-whatsapp-configs/:configId/edit"}
                      element={<AdminResellerWhatsAppConfigForm />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={STOCK_MOVEMENT_ROLES} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/stock"}
                      element={<AdminStockMovements />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={inventoryRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/inventory"}
                      element={<AdminInventory />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/inventory/resellers/:resellerAdminUserId"}
                      element={<AdminInventoryResellerStock />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={notificationRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/notifications"}
                      element={<AdminNotifications />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={reportsRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reports"}
                      element={<AdminReports />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={resellerPurchaseOrderRoles} redirectTo={process.env.PUBLIC_URL + "/admin"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-purchase-orders"}
                      element={<AdminResellerPurchaseOrders />}
                    />
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-purchase-orders/:orderId/view"}
                      element={<AdminResellerPurchaseOrderView />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={resellerPoInvoicePrintRoles} redirectTo={process.env.PUBLIC_URL + "/admin/orders"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-purchase-orders/:orderId/print"}
                      element={<AdminResellerPurchaseOrderInvoicePrint />}
                    />
                  </Route>
                  <Route element={<RoleRoute allowedRoles={resellerOrderEditRoles} redirectTo={process.env.PUBLIC_URL + "/admin/orders"} />}>
                    <Route
                      path={process.env.PUBLIC_URL + "/admin/reseller-purchase-orders/:orderId/edit"}
                      element={<AdminResellerPurchaseOrderEdit />}
                    />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFound/>} />
            </Routes>
          </Suspense>
          </AddToCartProvider>
        </ScrollToTop>
      </Router>
  );
};

export default App;
